/**
 * Asientos contables.
 *
 * Every journal entry is derived from exactly one event and carries that event's id. That
 * link is what makes the books auditable in both directions: from a number in a report back
 * to the sale that produced it, and from a sale forward to every account it touched.
 *
 * Entries are never edited. A correction is a new reversing entry, the same way a corrected
 * receipt is a new receipt.
 */

import { ZERO, addMoney, formatMoney, subMoney, type Centavos } from '@abastece/shared';
import { ACCOUNTS, accountByCode, esCodigoEnDolares, type AccountBalance, type AccountKey } from './accounts.js';

export interface JournalLine {
  cuenta: string;
  debe: Centavos;
  haber: Centavos;
  memo?: string;
  /**
   * USD cents, on a dollar account's line only (`bancos.ts` in shared): the dollars it moved, on
   * the same side as the córdobas it is carried at. What a dollar account holds in dollars is these
   * added up, as its balance in córdobas is the córdobas.
   */
  dolares?: number;
}

export interface JournalEntry {
  /** Same id as the event that produced it — one event, one entry. */
  eventId: string;
  fecha: string;
  concepto: string;
  lineas: JournalLine[];
}

export class UnbalancedEntryError extends Error {
  constructor(
    readonly entry: JournalEntry,
    readonly diff: Centavos,
  ) {
    super(
      `Asiento descuadrado por ${formatMoney(diff)}: ${entry.concepto} (evento ${entry.eventId})`,
    );
    this.name = 'UnbalancedEntryError';
  }
}

export function debit(account: AccountKey, amount: Centavos, memo?: string): JournalLine {
  return { cuenta: ACCOUNTS[account].code, debe: amount, haber: ZERO, ...(memo ? { memo } : {}) };
}

export function credit(account: AccountKey, amount: Centavos, memo?: string): JournalLine {
  return { cuenta: ACCOUNTS[account].code, debe: ZERO, haber: amount, ...(memo ? { memo } : {}) };
}

/** A dollar account's line, carrying the dollars it moved. */
export function conDolares(linea: JournalLine, dolares: number): JournalLine {
  return dolares !== 0 ? { ...linea, dolares } : linea;
}

export function totals(entry: JournalEntry): { debe: Centavos; haber: Centavos; diff: Centavos } {
  const debe = addMoney(...entry.lineas.map((l) => l.debe));
  const haber = addMoney(...entry.lineas.map((l) => l.haber));
  return { debe, haber, diff: subMoney(debe, haber) };
}

export function isBalanced(entry: JournalEntry): boolean {
  return totals(entry).diff === 0;
}

/**
 * Throws on an unbalanced entry rather than storing it.
 *
 * This is intentionally a hard failure. An unbalanced ledger discovered six months later is
 * unfixable in practice; one discovered at the moment it is produced is a bug with a stack
 * trace. Every reducer that emits an entry runs through here.
 */
export function assertBalanced(entry: JournalEntry): JournalEntry {
  const { diff } = totals(entry);
  if (diff !== 0) throw new UnbalancedEntryError(entry, diff);
  if (entry.lineas.length < 2) {
    throw new UnbalancedEntryError(entry, ZERO);
  }
  for (const l of entry.lineas) {
    if (!accountByCode(l.cuenta)) throw new Error(`Cuenta desconocida: ${l.cuenta}`);
    if (l.debe < 0 || l.haber < 0) throw new Error(`Montos negativos no permitidos en un asiento: ${l.cuenta}`);
    if (l.debe > 0 && l.haber > 0) throw new Error(`Una línea no puede tener debe y haber: ${l.cuenta}`);
    if (l.dolares !== undefined) {
      if (!esCodigoEnDolares(l.cuenta)) throw new Error(`Solo una cuenta en dólares lleva dólares: ${l.cuenta}`);
      if (!Number.isInteger(l.dolares) || l.dolares < 0) throw new Error(`Dólares negativos o con fracción en un asiento: ${l.cuenta}`);
    }
  }
  return entry;
}

export function entry(
  eventId: string,
  fecha: string,
  concepto: string,
  lineas: JournalLine[],
): JournalEntry {
  const conMonto = lineas.filter((l) => l.debe !== 0 || l.haber !== 0);
  /*
   * Nothing to book — goods that cost nothing, a bonus from the supplier, bulk with no cost on the
   * books repacked — is no entry at all (`applyEvent` drops it), and the goods still move. It used
   * to throw «Asiento descuadrado por C$ 0.00», and the purchase or the repack was refused whole.
   */
  if (conMonto.length === 0) return { eventId, fecha, concepto, lineas: [] };
  return assertBalanced({ eventId, fecha, concepto, lineas: conMonto });
}

/** Fold entries into per-account balances. Used by every financial report. */
export function balances(entries: readonly JournalEntry[]): Map<string, AccountBalance> {
  const out = new Map<string, AccountBalance>();
  for (const e of entries) {
    for (const l of e.lineas) {
      const acc = accountByCode(l.cuenta);
      if (!acc) continue;
      const cur = out.get(l.cuenta) ?? { code: l.cuenta, debe: ZERO, haber: ZERO, saldo: ZERO };
      cur.debe = addMoney(cur.debe, l.debe);
      cur.haber = addMoney(cur.haber, l.haber);
      if (l.dolares) cur.dolares = ((cur.dolares ?? 0) + (l.debe > 0 ? l.dolares : -l.dolares)) as Centavos;
      cur.saldo =
        acc.normal === 'debe' ? subMoney(cur.debe, cur.haber) : subMoney(cur.haber, cur.debe);
      out.set(l.cuenta, cur);
    }
  }
  return out;
}

/** The whole ledger must net to zero. Cheap to check, and it catches an entire class of bug. */
export function ledgerIsSquare(entries: readonly JournalEntry[]): boolean {
  let debe = 0;
  let haber = 0;
  for (const e of entries) {
    for (const l of e.lineas) {
      debe += l.debe;
      haber += l.haber;
    }
  }
  return debe === haber;
}
